import { inject } from '@angular/core';
import * as AlertActions from '@curbnturf/alert/src/lib/+state/alert.actions';
import { IBoondock, IPhoto, ISubPhoto, NGRX_NO_ACTION } from '@curbnturf/entities';
import { stringFromError } from '@curbnturf/form-helpers';
import { OfflineBoondockDbSet } from '@curbnturf/network';
import * as NetworkSelectors from '@curbnturf/network/src/lib/network/+state/network.selectors';
import { CaretakerService } from '@curbnturf/rating';
import { GlobalSpinnerService } from '@curbnturf/ui/src/lib/spinner/global-spinner.service';
import * as UserSelectors from '@curbnturf/user/src/lib/+state/user.selectors';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { BoondockService } from '../boondock.service';
import { OfflineBoondockService } from '../offline-boondock.service';
import * as BoondockActions from './boondock.actions';
import * as BoondockSelectors from './boondock.selectors';

export const createBoondock$ = createEffect(
  (
    actions$ = inject(Actions),
    boondockService = inject(BoondockService),
    store = inject(Store),
    offlineBoondockService = inject(OfflineBoondockService),
  ) =>
    actions$.pipe(
      ofType(BoondockActions.createBoondock),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      withLatestFrom(store.select(UserSelectors.getCurrentUser)),
      switchMap(([[action, status], currentUser]) => {
        const boondock: IBoondock = { ...action.boondock, user: currentUser };
        if (status.connected) {
          return boondockService.create(boondock).pipe(
            mergeMap((createdBoondock: IBoondock) => [
              BoondockActions.boondockCreated({ boondock: createdBoondock }),
              BoondockActions.boondockLoaded({ boondock: createdBoondock }),
              BoondockActions.selectBoondock({ boondockId: createdBoondock.id }),
            ]),
            catchError((err) =>
              of(
                BoondockActions.createBoondockFailed({
                  error: err?.error?.message || err?.message || err || 'Error Creating Boondock',
                }),
              ),
            ),
          );
        } else {
          // If Offline, use the alternate
          return offlineBoondockService.create(boondock).pipe(
            mergeMap((createdBoondock: IBoondock) => [
              BoondockActions.boondockCreated({ boondock: createdBoondock }),
              BoondockActions.boondockLoaded({ boondock: createdBoondock }),
              BoondockActions.selectBoondock({ boondockId: createdBoondock.syncId }),
            ]),
            catchError((err) =>
              of(
                BoondockActions.createBoondockFailed({
                  error: err?.error?.message || err?.message || err || 'Error Creating Boondock',
                }),
              ),
            ),
          );
        }
      }),
    ),
  { functional: true },
);

export const removeImageFromBoondock$ = createEffect(
  (actions$ = inject(Actions), boondockService = inject(BoondockService)) =>
    actions$.pipe(
      ofType(BoondockActions.removeImageFromBoondock),
      switchMap((action) =>
        boondockService.removeImage(action.boondockId, action.index).pipe(
          mergeMap((photos: IPhoto[]) => {
            return [
              BoondockActions.boondockImagesUpdated({ id: action.boondockId, photos }),
              AlertActions.displayAlert({
                title: 'Image Removed from Boondock',
                body: 'Removed Image',
                level: 'error',
              }),
            ];
          }),
          catchError((err) =>
            of(
              BoondockActions.createBoondockFailed({
                error: err?.error?.message || err?.message || err || 'Error Removing Image From Boondock',
              }),
            ),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const removeBoondock$ = createEffect(
  (
    actions$ = inject(Actions),
    boondockService = inject(BoondockService),
    store = inject(Store),
    offlineBoondockService = inject(OfflineBoondockService),
  ) =>
    actions$.pipe(
      ofType(BoondockActions.removeBoondock),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      switchMap(([action, status]) => {
        if (status.connected) {
          return boondockService.remove(action.boondockId as number).pipe(
            switchMap((removedBoondock: IBoondock) => {
              const actionArray: Action[] = [
                BoondockActions.boondockRemoved({
                  boondock: removedBoondock,
                }),
              ];

              if (removedBoondock) {
                actionArray.push(
                  AlertActions.displayAlert({
                    title: 'Boondock removed',
                    body: 'Removed Boondock',
                    level: 'error',
                  }),
                );
              }

              return actionArray;
            }),
            catchError((err) => [
              BoondockActions.removeBoondockFailed({
                error: err?.error?.message || err?.message || err || 'Error Removing Boondock',
              }),
            ]),
          );
        } else if (!status.connected) {
          return offlineBoondockService.remove(action.boondockId as string).pipe(
            switchMap((removedBoondock: IBoondock) => {
              const actionArray: Action[] = [
                BoondockActions.boondockRemoved({
                  boondock: removedBoondock,
                }),
              ];

              if (removedBoondock) {
                actionArray.push(
                  AlertActions.displayAlert({
                    title: 'Boondock removed',
                    body: 'Removed Boondock',
                    level: 'error',
                  }),
                );
              }

              return actionArray;
            }),
            catchError((err) => [
              BoondockActions.removeBoondockFailed({
                error: err?.error?.message || err?.message || err || 'Error Removing Boondock',
              }),
            ]),
          );
        }

        return of(
          BoondockActions.removeBoondockFailed({
            error: 'Error Removing Boondock - Could Not Match Connection State',
          }),
        );
      }),
      catchError((err) =>
        of(
          BoondockActions.removeBoondockFailed({
            error:
              err?.error?.message || err?.message || err || 'Error Removing Boondock - Could Not Match Connection State',
          }),
        ),
      ),
    ) as Observable<Action>,
  { functional: true },
);

export const restoreBoondock$ = createEffect(
  (actions$ = inject(Actions), boondockService = inject(BoondockService)) =>
    actions$.pipe(
      ofType(BoondockActions.restoreBoondock),
      switchMap((action) =>
        boondockService.restore(action.boondockId).pipe(
          switchMap((restoredBoondock: IBoondock) => {
            const actionArray: Action[] = [BoondockActions.boondockRestored({ boondock: restoredBoondock })];

            if (restoredBoondock) {
              actionArray.push(
                AlertActions.displayAlert({
                  title: 'Boondock restored',
                  body: 'Restored Boondock',
                  level: 'error',
                }),
              );
            }

            return actionArray;
          }),
          catchError((err) =>
            of(
              BoondockActions.createBoondockFailed({
                error: err?.error?.message || err?.message || err || 'Error Restoring Boondock',
              }),
            ),
          ),
        ),
      ),
      catchError((err) =>
        of(
          BoondockActions.createBoondockFailed({ error: err?.error?.message || err?.message || err || 'Error Restoring Boondock' }),
        ),
      ),
    ) as Observable<Action>,
  { functional: true },
);

export const loadBoondock$ = createEffect(
  (
    actions$ = inject(Actions),
    boondockService = inject(BoondockService),
    store = inject(Store),
    offlineBoondockService = inject(OfflineBoondockService),
  ) =>
    actions$.pipe(
      ofType(BoondockActions.loadBoondock),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      switchMap(([action, status]) => {
        if (status.connected && typeof action.boondockId === 'number') {
          return boondockService.fetch(action.boondockId).pipe(
            switchMap((boondock: IBoondock) => [BoondockActions.boondockLoaded({ boondock }), BoondockActions.selectBoondock({ boondockId: boondock.id })]),
            catchError((err) =>
              of(
                BoondockActions.loadBoondockFailed({
                  error: err?.error?.message || err?.message || err || 'Error Loading Boondock',
                }),
              ),
            ),
          );
        } else if (typeof action.boondockId === 'string') {
          return offlineBoondockService.fetch(action.boondockId).pipe(
            switchMap((boondock: IBoondock) => [
              BoondockActions.boondockLoaded({ boondock }),
              BoondockActions.selectBoondock({ boondockId: boondock.syncId }),
            ]),
            catchError((err) =>
              of(
                BoondockActions.loadBoondockFailed({
                  error: err?.error?.message || err?.message || err || 'Error Loading Boondock',
                }),
              ),
            ),
          );
        } else {
          return of(NGRX_NO_ACTION);
        }
      }),
      catchError((err) =>
        of(BoondockActions.loadBoondockFailed({ error: err?.error?.message || err?.message || err || 'Error Loading Boondock' })),
      ),
    ),
  { functional: true },
);

export const loadBoondocks$ = createEffect(
  (actions$ = inject(Actions), boondockService = inject(BoondockService), store = inject(Store)) =>
    actions$.pipe(
      ofType(BoondockActions.loadBoondocks),
      switchMap((action) => {
        if (action.payload) {
          return boondockService.fetchAll(action.payload).pipe(
            switchMap((result: { total: number; page: number; boondocks: IBoondock[] }) => [
              BoondockActions.boondocksLoaded({ boondocks: result.boondocks }),
            ]),
            catchError((err) =>
              of(
                BoondockActions.loadBoondockFailed({
                  error: err?.error?.message || err?.message || err || 'Error Loading Boondocks',
                }),
              ),
            ),
          );
        }

        return store.pipe(select(UserSelectors.getCurrentId)).pipe(
          filter((userId) => !!userId),
          take(1),
          switchMap((userId) => {
            if (!userId) {
              return [BoondockActions.loadBoondockFailed({ error: 'No user id provided' })];
            }

            return boondockService.fetchAll({ userId }).pipe(
              switchMap((result: { total: number; page: number; boondocks: IBoondock[] }) => [
                BoondockActions.boondocksLoaded({ boondocks: result.boondocks }),
              ]),
              catchError((err) => [
                BoondockActions.loadBoondockFailed({ error: err?.error?.message || err || 'Error Loading Boondocks' }),
              ]),
            );
          }),
          catchError((err) => [
            BoondockActions.loadBoondockFailed({ error: err?.error?.message || err?.message || err || 'Error Loading Boondocks' }),
          ]),
        );
      }),
      catchError((err) => [BoondockActions.loadBoondockFailed({ error: err?.error?.message || err || 'Error Loading Boondocks' })]),
    ),
  { functional: true },
);

// using mergeMap to allow multiple updates at a time
export const updateBoondock$ = createEffect(
  (actions$ = inject(Actions), boondockService = inject(BoondockService), store = inject(Store)) =>
    actions$.pipe(
      ofType(BoondockActions.updateBoondock),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([action, status]) => {
        if (status.connected) {
          return boondockService.update(action.boondock).pipe(
            mergeMap((updatedBoondock: IBoondock) => [BoondockActions.boondockLoaded({ boondock: updatedBoondock })]),
            catchError((err) => [
              BoondockActions.updateBoondockFailed({ error: err?.error?.message || err || 'Error Updating Boondock' }),
            ]),
          );
        } else {
          // If Offline, we handle this elsewhere
          return [NGRX_NO_ACTION];
        }
      }),
      catchError((err) => [
        BoondockActions.updateBoondockFailed({ error: err?.error?.message || err || 'Error Updating Boondock' }),
      ]),
    ),
  { functional: true },
);

// using mergeMap to allow multiple updates at a time
export const updateBoondockOffline$ = createEffect(
  (actions$ = inject(Actions), offlineBoondockDbSet = inject(OfflineBoondockDbSet)) =>
    actions$.pipe(
      ofType(BoondockActions.updateBoondockOffline),
      mergeMap((action) => {
        return of(offlineBoondockDbSet.store(action.payload)).pipe(
          mergeMap(() => [NGRX_NO_ACTION]),
          catchError((err) => [
            BoondockActions.updateBoondockFailed({ error: err?.error?.message || err || 'Error Updating Boondock' }),
          ]),
        );
      }),
      catchError((err) => [
        BoondockActions.updateBoondockFailed({ error: err?.error?.message || err || 'Error Updating Boondock' }),
      ]),
    ),
  { functional: true },
);

export const enableBoondock$ = createEffect(
  (actions$ = inject(Actions), boondockService = inject(BoondockService)) =>
    actions$.pipe(
      ofType(BoondockActions.enableBoondock),
      switchMap((action) => {
        if (action.boondock) {
          return boondockService.enable(action.boondock).pipe(
            mergeMap((result: { success: boolean; message: string }) => {
              if (result.success && action.boondock?.id) {
                return [
                  AlertActions.displayAlert({
                    title: 'Listing Published',
                    body: `Published ${action.boondock.name}`,
                  }),
                  BoondockActions.boondockEnabled({ boondockId: action.boondock.id }),
                ];
              }
              return [
                BoondockActions.boondockEnabledFailed({
                  body: `Unable to publish listing for the following reasons:\n${result.message}`,
                  boondockId: action.boondock.id,
                }),
              ];
            }),
            catchError((err) => [
              BoondockActions.boondockEnabledFailed({
                body: err?.error?.message || err?.message || err || 'Error Enabling Boondock',
              }),
            ]),
          );
        } else {
          return [
            BoondockActions.boondockEnabledFailed({
              body: 'Boondock Record Not Provided',
            }),
          ];
        }
      }),
      catchError((err) => [
        BoondockActions.boondockEnabledFailed({
          body: err?.error?.message || err?.message || err || 'Error Enabling Boondock',
        }),
      ]),
    ),
  { functional: true },
);

export const disableBoondock$ = createEffect(
  (actions$ = inject(Actions), boondockService = inject(BoondockService)) =>
    actions$.pipe(
      ofType(BoondockActions.disableBoondock),
      switchMap((action) =>
        boondockService.disable(action.boondock).pipe(
          mergeMap((result: { success: boolean; message: string }) => {
            if (result.success && action.boondock.id) {
              return [
                AlertActions.displayAlert({
                  title: 'Listing Disabled',
                  body: `Disabled ${action.boondock.name}`,
                }),
                BoondockActions.boondockDisabled({
                  boondockId: action.boondock.id,
                }),
              ];
            }
            return [
              BoondockActions.boondockDisabledFailed({
                error: `Unable to disable listing for the following reasons:\n${result.message}`,
              }),
            ];
          }),
          catchError((err) => [
            BoondockActions.boondockDisabledFailed({
              error: err?.error?.message || err?.message || err || 'Error Disabling Boondock',
            }),
          ]),
        ),
      ),
      catchError((err) => [
        BoondockActions.boondockEnabledFailed({
          body: err?.error?.message || err?.message || err || 'Error Enabling Boondock',
        }),
      ]),
    ),
  { functional: true },
);

export const createBoondockAmenity$ = createEffect(
  (actions$ = inject(Actions), boondockService = inject(BoondockService)) =>
    actions$.pipe(
      ofType(BoondockActions.createAmenity),
      switchMap((action) =>
        boondockService.createAmenity(action.boondock).pipe(
          mergeMap((updatedBoondock: IBoondock) => [
            BoondockActions.boondockLoaded({
              boondock: updatedBoondock,
            }),
          ]),
          catchError((err) => [
            BoondockActions.updateBoondockFailed({
              error: err?.error?.message || err?.message || err || 'Error Creating Boondock Amenity',
            }),
          ]),
        ),
      ),
      catchError((err) => [
        BoondockActions.updateBoondockFailed({
          error: err?.error?.message || err?.message || err || 'Error Creating Boondock Amenity',
        }),
      ]),
    ),
  { functional: true },
);

export const removeBoondockAmenity$ = createEffect(
  (actions$ = inject(Actions), boondockService = inject(BoondockService)) =>
    actions$.pipe(
      ofType(BoondockActions.removeAmenity),
      switchMap((action) =>
        boondockService.removeAmenity(action.boondock, action.amenity).pipe(
          mergeMap((updatedBoondock: IBoondock) => [
            BoondockActions.boondockLoaded({
              boondock: updatedBoondock,
            }),
          ]),
          catchError((err) => [
            BoondockActions.updateBoondockFailed({
              error: err?.error?.message || err?.message || err || 'Error Removing Boondock Amenity',
            }),
          ]),
        ),
      ),
      catchError((err) => [
        BoondockActions.updateBoondockFailed({
          error: err?.error?.message || err?.message || err || 'Error Removing Boondock Amenity',
        }),
      ]),
    ),
  { functional: true },
);

export const createBoondockSignalReception$ = createEffect(
  (actions$ = inject(Actions), boondockService = inject(BoondockService)) =>
    actions$.pipe(
      ofType(BoondockActions.createSignalReception),
      switchMap((action) =>
        boondockService.createSignalReception(action.boondock).pipe(
          mergeMap((updatedBoondock: IBoondock) => [
            BoondockActions.boondockLoaded({
              boondock: updatedBoondock,
            }),
          ]),
          catchError((err) => [
            BoondockActions.updateBoondockFailed({
              error: err?.error?.message || err?.message || err || 'Error Creating Boondock Signal Reception',
            }),
          ]),
        ),
      ),
      catchError((err) => [
        BoondockActions.updateBoondockFailed({
          error: err?.error?.message || err?.message || err || 'Error Creating Boondock Signal Reception',
        }),
      ]),
    ),
  { functional: true },
);

export const removeBoondockSignalReception$ = createEffect(
  (actions$ = inject(Actions), boondockService = inject(BoondockService)) =>
    actions$.pipe(
      ofType(BoondockActions.removeSignalReception),
      switchMap((action) =>
        boondockService.removeSignalReception(action.boondock, action.signalReception).pipe(
          mergeMap((updatedBoondock: IBoondock) => [
            BoondockActions.boondockLoaded({
              boondock: updatedBoondock,
            }),
          ]),
          catchError((err) => [
            BoondockActions.updateBoondockFailed({
              error: err?.error?.message || err?.message || err || 'Error Removing Boondock Signal Reception',
            }),
          ]),
        ),
      ),
      catchError((err) => [
        BoondockActions.updateBoondockFailed({
          error: err?.error?.message || err?.message || err || 'Error Removing Boondock Signal Reception',
        }),
      ]),
    ),
  { functional: true },
);

export const uploadBoondockImage$ = createEffect(
  (
    actions$ = inject(Actions),
    boondockService = inject(BoondockService),
    store = inject(Store),
    spinnerService = inject(GlobalSpinnerService),
    offlineBoondockService = inject(OfflineBoondockService),
  ) =>
    actions$.pipe(
      ofType(BoondockActions.uploadBoondockImage),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      switchMap(([action, status]) => {
        const payload = action;
        if (!payload || !payload.formData || !payload.data || (!payload.data.boondockId && !payload.data.syncId)) {
          return of(NGRX_NO_ACTION);
        }
        if (status.connected && payload.data.boondockId) {
          const files = payload.formData;
          const boondockId = payload.data.boondockId;
          spinnerService.spinnerOn();
          return boondockService.uploadImage(boondockId, files).pipe(
            switchMap((results: { id: number; photos: IPhoto[] }) => {
              spinnerService.spinnerOff();
              return [
                BoondockActions.boondockImagesUpdated({
                  ...results,
                }),
              ];
            }),
            catchError((err) => {
              spinnerService.spinnerOff();

              return [
                BoondockActions.createBoondockFailed({
                  error: err?.error?.message || err?.message || err || 'Error Uploading Boondock Image',
                }),
              ];
            }),
          );
        } else if (!status.connected && payload.data.syncId) {
          const files = payload.formData;
          const syncId = payload.data.syncId;
          spinnerService.spinnerOn();
          return offlineBoondockService.uploadImage(syncId, files).pipe(
            switchMap((results: { syncId: string; photos: IPhoto[] }) => {
              spinnerService.spinnerOff();
              return [
                BoondockActions.boondockImagesUpdated({
                  ...results,
                }),
              ];
            }),
            catchError((err) => {
              spinnerService.spinnerOff();

              return [
                BoondockActions.createBoondockFailed({
                  error: err?.error?.message || err?.message || err || 'Error Uploading Boondock Image',
                }),
              ];
            }),
          );
        } else {
          return of(NGRX_NO_ACTION);
        }
      }),
      catchError((err) => {
        spinnerService.spinnerOff();

        return [
          BoondockActions.createBoondockFailed({
            error: err?.error?.message || err?.message || err || 'Error Uploading Boondock Image',
          }),
        ];
      }),
    ),
  { functional: true },
);

export const recropBoondockImage$ = createEffect(
  (actions$ = inject(Actions), boondockService = inject(BoondockService), store = inject(Store)) =>
    actions$.pipe(
      ofType(BoondockActions.recropImage),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      switchMap(([action, status]) => {
        if (!action || !action.boondockToSave || !action.boondockToSave.photos || !action.photo) {
          return of(NGRX_NO_ACTION);
        }

        const boondock: IBoondock = { ...action.boondockToSave, photos: [...action.boondockToSave.photos] };
        const photo: ISubPhoto = action.photo;
        if (boondock.photos) {
          const foundPhotoIndex = boondock.photos.findIndex((el) => el.key === photo.key);
          if (foundPhotoIndex >= 0) {
            const newPhoto = { ...photo, key: boondock.photos[foundPhotoIndex].key };
            boondock.photos.splice(foundPhotoIndex, 1, newPhoto);
          } else {
            return of(BoondockActions.recropImageFailure({ error: 'Failed to find the image to crop.' }));
          }
        } else {
          return of(BoondockActions.recropImageFailure({ error: 'Failed to find the image to crop.' }));
        }

        if (status.connected && boondock.id) {
          return boondockService.update(boondock).pipe(
            mergeMap((updatedBoondock: IBoondock) => [
              BoondockActions.boondockLoaded({ boondock: updatedBoondock }),
              BoondockActions.boondockImagesUpdated({ id: updatedBoondock.id, photos: updatedBoondock.photos ?? [] }),
            ]),
            catchError((err) => [
              BoondockActions.updateBoondockFailed({ error: err?.error?.message || err || 'Error Updating Boondock' }),
            ]),
          );
        } else {
          return of(NGRX_NO_ACTION);
        }
      }),
      catchError((err) => {
        return [
          BoondockActions.createBoondockFailed({
            error: err?.error?.message || err?.message || err || 'Error Uploading Boondock Image',
          }),
        ];
      }),
    ),
  { functional: true },
);

export const uploadBoondockReviewImage$ = createEffect(
  (
    actions$ = inject(Actions),
    caretakerService = inject(CaretakerService),
    spinnerService = inject(GlobalSpinnerService),
  ) =>
    actions$.pipe(
      ofType(BoondockActions.uploadBoondockReviewImage),
      switchMap((action) => {
        if (!action || !action.formData || !action.data || !action.data.boondockId) {
          return of(NGRX_NO_ACTION);
        }
        const files = action.formData;
        const boondockId = action.data.boondockId;
        spinnerService.spinnerOn();
        return caretakerService.uploadImage(boondockId, files).pipe(
          switchMap((results: { id: number; photos: IPhoto[] }) => {
            spinnerService.spinnerOff();
            return [
              BoondockActions.boondockReviewImageUploaded({
                ...results,
              }),
            ];
          }),
          catchError((err) => {
            spinnerService.spinnerOff();

            return [
              BoondockActions.boondockReviewImageUploadFailed({
                error: err?.error?.message || err?.message || err || 'Error Uploading Boondock Review Image',
              }),
            ];
          }),
        );
      }),
      catchError((err) => {
        spinnerService.spinnerOff();

        return [
          BoondockActions.boondockReviewImageUploadFailed({
            error: err?.error?.message || err?.message || err || 'Error Uploading Boondock Review Image',
          }),
        ];
      }),
    ),
  { functional: true },
);

export const selectBoondock$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(BoondockActions.selectBoondock),
      withLatestFrom(store.select(BoondockSelectors.getBoondocks)),
      switchMap(([action, boondocks]) => {
        if (!action.boondockId) {
          return [BoondockActions.boondockSelected({ boondock: undefined })];
        }

        const id = action.boondockId;
        const foundBoondock = boondocks.find(
          (el: IBoondock) => (el.id && id && el.id === id) || (el.syncId && id && el.syncId === id),
        );
        if (foundBoondock) {
          return [BoondockActions.boondockSelected({ boondock: foundBoondock })];
        }
        return [BoondockActions.loadBoondock({ boondockId: id })];
      }),
      catchError((err) =>
        of(BoondockActions.loadBoondockFailed({ error: err?.error?.message || err?.message || err || 'Error Selecting Boondock' })),
      ),
    ),
  { functional: true },
);

export const createBoondockFailed$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(BoondockActions.createBoondockFailed),
      map((action) =>
        AlertActions.displayAlert({
          title: 'Error Creating Boondock',
          body: stringFromError(action.error),
          level: 'error',
        }),
      ),
    ),
  { functional: true },
);

export const removeBoondockFailed$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(BoondockActions.removeBoondockFailed),
      map((action) =>
        AlertActions.displayAlert({
          title: 'Error Removing Boondock',
          body: stringFromError(action.error),
          level: 'error',
        }),
      ),
    ),
  { functional: true },
);

export const loadBoondockFailed$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(BoondockActions.loadBoondockFailed),
      map((action) =>
        AlertActions.displayAlert({
          title: 'Error Loading Boondocks',
          body: stringFromError(action.error),
          level: 'error',
        }),
      ),
    ),
  { functional: true },
);

export const updateBoondockFailed$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(BoondockActions.updateBoondockFailed),
      map((action) =>
        AlertActions.displayAlert({
          title: 'Error Updating Boondock',
          body: stringFromError(action.error),
          level: 'error',
        }),
      ),
    ),
  { functional: true },
);

export const boondockEnabledFailed$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(BoondockActions.boondockEnabledFailed),
      map((action) =>
        AlertActions.displayAlert({
          title: 'Error Enabling Listing',
          body: action.body?.split('\n').join('<br> - '),
          level: 'error',
          noTimeout: true,
        }),
      ),
    ),
  { functional: true },
);

export const boondockDisabledFailed$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(BoondockActions.boondockDisabledFailed),
      map((action) =>
        AlertActions.displayAlert({
          title: 'Error Disabling Listing',
          body: action.error?.split('\n').join('<br> - '),
          level: 'error',
        }),
      ),
    ),
  { functional: true },
);

export const favoriteBoondock$ = createEffect(
  (actions$ = inject(Actions), boondockService = inject(BoondockService)) =>
    actions$.pipe(
      ofType(BoondockActions.favoriteBoondock),
      switchMap((action) => {
        if (!action.point) {
          return [
            BoondockActions.listingFavorFailed({
              error: 'No boondock to favorite',
            }),
          ];
        }

        return boondockService.favorite(action.point).pipe(
          mergeMap((successResult: { success: boolean; message: string }) => {
            if (!successResult.success) {
              return [
                BoondockActions.listingFavorFailed({
                  error: successResult.message,
                }),
              ];
            }

            return [
              BoondockActions.listingFavorCompleted({
                id: action.point.id || 0,
                favored: true,
              }),
            ];
          }),
          catchError((err) => [
            BoondockActions.listingFavorFailed({
              error: err?.error?.message || err?.message || err || 'Error Favoriting Boondock',
            }),
          ]),
        );
      }),
      catchError((err) => [
        BoondockActions.listingFavorFailed({
          error: err?.error?.message || err?.message || err || 'Error Favoriting Boondock',
        }),
      ]),
    ),
  { functional: true },
);

export const unfavoriteBoondock$ = createEffect(
  (actions$ = inject(Actions), boondockService = inject(BoondockService)) =>
    actions$.pipe(
      ofType(BoondockActions.unfavoriteBoondock),
      switchMap((action) => {
        if (!action.point) {
          return [
            BoondockActions.listingFavorFailed({
              error: 'No boondock to unfavorite',
            }),
          ];
        }

        return boondockService.unfavorite(action.point).pipe(
          mergeMap((successResult: { success: boolean; message: string }) => {
            if (!successResult.success) {
              return [
                BoondockActions.listingFavorFailed({
                  error: successResult.message,
                }),
              ];
            }

            return [BoondockActions.listingFavorCompleted({ id: action.point.id || 0, favored: false })];
          }),
          catchError((err) => [
            BoondockActions.listingFavorFailed({
              error: err?.error?.message || err?.message || err || 'Error Unfavoring Boondock',
            }),
          ]),
        );
      }),
      catchError((err) => [
        BoondockActions.listingFavorFailed({
          error: err?.error?.message || err?.message || err || 'Error Unfavoring Boondock',
        }),
      ]),
    ),
  { functional: true },
);
